import { AlertTriangle, ArrowLeft, CheckCircle2, Info, Loader2, X, XOctagon } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------------------------
// Shared class recipes
// ---------------------------------------------------------------------------------------------

/** Selected state for toggles, chips and filters: black on light, off-white on dark. */
export const toggleOn = 'border-stone-900 bg-stone-900 text-white dark:border-stone-100 dark:bg-stone-100 dark:text-stone-900';
/** Unselected counterpart of `toggleOn`. */
export const toggleOff =
  'border-stone-300 bg-white text-stone-700 hover:border-stone-500 hover:text-stone-950 dark:border-stone-700 dark:bg-ink-900 dark:text-stone-300 dark:hover:border-stone-500 dark:hover:text-white';
/** Ruled list: rows separated by hairlines, no enclosing box. */
export const ruledList = 'divide-y divide-stone-200 border-y border-stone-200 dark:divide-stone-800 dark:border-stone-800';
/** Hover treatment for a clickable row inside `ruledList`. */
export const rowHover = 'transition-colors hover:bg-stone-100 dark:hover:bg-ink-900';
/** Inline text link. */
export const textLink =
  'font-medium text-stone-950 underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-950 dark:text-white dark:decoration-stone-600 dark:hover:decoration-white';

// ---------------------------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-stone-900 text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-white',
  secondary: 'bg-stone-200/80 text-stone-900 hover:bg-stone-300/70 dark:bg-ink-800 dark:text-stone-100 dark:hover:bg-stone-800',
  outline:
    'border border-stone-300 bg-white text-stone-900 hover:border-stone-500 dark:border-stone-700 dark:bg-transparent dark:text-stone-100 dark:hover:border-stone-500',
  ghost: 'text-stone-700 hover:bg-stone-200/60 hover:text-stone-950 dark:text-stone-300 dark:hover:bg-ink-800 dark:hover:text-white',
  danger: 'bg-red-700 text-white hover:bg-red-800',
};

const buttonSizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-base gap-2',
};

const buttonBase =
  'inline-flex shrink-0 items-center justify-center rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, className, children, disabled, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(buttonBase, buttonVariants[variant], buttonSizes[size], className)}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});

export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  className,
  children,
  icon,
}: {
  to: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <Link to={to} className={cn(buttonBase, buttonVariants[variant], buttonSizes[size], className)}>
      {icon}
      {children}
    </Link>
  );
}

export function IconButton({
  label,
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-10 items-center justify-center rounded-md text-stone-500 transition-colors hover:bg-stone-200/60 hover:text-stone-950 dark:text-stone-400 dark:hover:bg-ink-800 dark:hover:text-white',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------------------------

export function Card({ className, children, as: As = 'div' }: { className?: string; children: ReactNode; as?: 'div' | 'section' | 'article' }) {
  return <As className={cn('rounded-md border border-stone-200 bg-white dark:border-stone-800 dark:bg-ink-900', className)}>{children}</As>;
}

export function CardHeader({ title, subtitle, action, icon }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 border-b border-stone-200 px-4 py-3 dark:border-stone-800">
      {icon && <div className="mt-0.5 text-stone-400 dark:text-stone-500 [&_svg]:size-4">{icon}</div>}
      <div className="min-w-0 flex-1">
        <h2 className="text-[15px] font-semibold leading-snug">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-stone-500 dark:text-stone-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  back,
  eyebrow,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string };
  eyebrow?: ReactNode;
}) {
  return (
    <header className="mb-6">
      {back && (
        <Link
          to={back.to}
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-950 dark:text-stone-400 dark:hover:text-white"
        >
          <ArrowLeft className="size-3.5" />
          {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-stone-300 pb-4 dark:border-stone-700">
        <div className="min-w-0">
          {eyebrow && <div className="label-caps mb-1.5 text-stone-500 dark:text-stone-400">{eyebrow}</div>}
          <h1 className="text-[1.75rem] font-semibold leading-tight tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1 max-w-2xl text-stone-600 dark:text-stone-400">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/** Small uppercase heading over a list or group, with an optional trailing link or control. */
export function SectionTitle({ children, action, className }: { children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('mb-2 flex items-baseline justify-between gap-3', className)}>
      <h2 className="label-caps text-stone-500 dark:text-stone-400">{children}</h2>
      {action && <div className="text-sm">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------------------------

const controlBase =
  'w-full rounded-md border border-stone-300 bg-white px-3 text-stone-900 placeholder:text-stone-400 hover:border-stone-400 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900 disabled:opacity-60 dark:border-stone-700 dark:bg-ink-900 dark:text-stone-100 dark:placeholder:text-stone-500 dark:hover:border-stone-600 dark:focus:border-stone-200 dark:focus:ring-stone-200';

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-stone-800 dark:text-stone-200">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
      ) : (
        hint && <p className="text-xs text-stone-500 dark:text-stone-400">{hint}</p>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cn(controlBase, 'h-10', className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...props },
  ref,
) {
  return <textarea ref={ref} className={cn(controlBase, 'py-2', className)} {...props} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...props },
  ref,
) {
  return (
    <select ref={ref} className={cn(controlBase, 'h-10 pr-8', className)} {...props}>
      {children}
    </select>
  );
});

/** Large numeric entry optimized for gloved thumbs; `unit` renders inside the field. */
export function NumberInput({
  label,
  value,
  onChange,
  unit,
  min,
  max,
  step = 'any',
  placeholder,
  hint,
  className,
}: {
  label: ReactNode;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  unit?: ReactNode;
  min?: number;
  max?: number;
  step?: number | 'any';
  placeholder?: string;
  hint?: ReactNode;
  className?: string;
}) {
  const id = useId();
  const outOfRange = value !== undefined && ((min !== undefined && value < min) || (max !== undefined && value > max));
  return (
    <Field label={label} htmlFor={id} hint={hint} error={outOfRange ? `Expected ${min ?? ''}–${max ?? ''}` : null} className={className}>
      <div className="relative">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step={step}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') return onChange(undefined);
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : undefined);
          }}
          className={cn(controlBase, 'h-12 text-lg font-semibold tabular-nums', unit ? 'pr-14' : '', outOfRange && 'border-amber-500')}
        />
        {unit && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center font-mono text-sm text-stone-500">{unit}</span>
        )}
      </div>
    </Field>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className={cn('flex cursor-pointer items-start justify-between gap-4', disabled && 'cursor-not-allowed opacity-60')}>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {description && <span className="block text-sm text-stone-500 dark:text-stone-400">{description}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full transition-colors',
          checked ? 'bg-stone-900 dark:bg-stone-100' : 'bg-stone-300 dark:bg-stone-700',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-5 rounded-full transition-transform',
            checked ? 'translate-x-5.5 bg-white dark:bg-ink-950' : 'translate-x-0.5 bg-white dark:bg-stone-300',
          )}
        />
      </button>
    </label>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  className,
  ariaLabel,
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  className?: string;
  ariaLabel?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('inline-flex rounded-md border border-stone-300 bg-white p-0.5 dark:border-stone-700 dark:bg-ink-900', className)}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'flex-1 whitespace-nowrap rounded-[4px] font-medium transition-colors',
            size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
            value === o.value
              ? 'bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900'
              : 'text-stone-600 hover:text-stone-950 dark:text-stone-400 dark:hover:text-white',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Scored option list used by GCS, NIHSS, LAMS, APGAR and similar scales. */
export function ChoiceList<T extends string | number>({
  options,
  value,
  onChange,
  name,
}: {
  options: { value: T; label: ReactNode; meta?: ReactNode }[];
  value: T | undefined;
  onChange: (value: T) => void;
  name: string;
}) {
  return (
    <div role="radiogroup" aria-label={name} className="grid gap-1">
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(o.value)}
            className={cn(
              'flex min-h-11 items-center gap-3 rounded-md border px-2.5 py-2 text-left text-sm transition-colors',
              selected
                ? 'border-stone-900 bg-stone-100 font-medium text-stone-950 dark:border-stone-200 dark:bg-ink-800 dark:text-white'
                : 'border-stone-200 bg-white hover:border-stone-400 dark:border-stone-800 dark:bg-ink-900 dark:hover:border-stone-600',
            )}
          >
            {o.meta !== undefined && (
              <span
                className={cn(
                  'flex h-7 min-w-7 shrink-0 items-center justify-center rounded-[4px] px-1 font-mono text-sm font-semibold tabular-nums',
                  selected ? 'bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900' : 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300',
                )}
              >
                {o.meta}
              </span>
            )}
            <span className="flex-1">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------------------------

type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'purple';

const badgeTones: Record<Tone, string> = {
  neutral: 'bg-stone-200/70 text-stone-700 dark:bg-stone-800 dark:text-stone-300',
  // "brand" is an outlined label (routes, categories, population) so rows of them stay quiet.
  brand: 'text-stone-800 ring-1 ring-inset ring-stone-300 dark:text-stone-200 dark:ring-stone-600',
  success: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-400/15 dark:text-emerald-300',
  warning: 'bg-amber-100 text-amber-900 dark:bg-amber-400/15 dark:text-amber-300',
  danger: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  purple: 'bg-violet-100 text-violet-900 dark:bg-violet-400/15 dark:text-violet-300',
};

export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-[4px] px-1.5 py-px text-xs font-medium', badgeTones[tone], className)}>
      {children}
    </span>
  );
}

const alertStyles = {
  info: { cls: 'border-stone-400 bg-stone-100 text-stone-800 dark:border-stone-500 dark:bg-ink-800 dark:text-stone-200', icon: 'text-stone-500', Icon: Info },
  success: {
    cls: 'border-emerald-600 bg-emerald-50 text-emerald-950 dark:border-emerald-500 dark:bg-emerald-500/10 dark:text-emerald-100',
    icon: 'text-emerald-600 dark:text-emerald-400',
    Icon: CheckCircle2,
  },
  warning: {
    cls: 'border-amber-500 bg-amber-50 text-amber-950 dark:border-amber-400 dark:bg-amber-400/10 dark:text-amber-100',
    icon: 'text-amber-600 dark:text-amber-400',
    Icon: AlertTriangle,
  },
  danger: {
    cls: 'border-red-600 bg-red-50 text-red-950 dark:border-red-500 dark:bg-red-500/10 dark:text-red-100',
    icon: 'text-red-600 dark:text-red-400',
    Icon: XOctagon,
  },
};

export function Alert({
  tone = 'info',
  title,
  children,
  action,
  className,
}: {
  tone?: keyof typeof alertStyles;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const { cls, icon, Icon } = alertStyles[tone];
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-r-md border-l-[3px] py-2.5 pl-3 pr-3', cls, className)}
    >
      <Icon className={cn('mt-0.5 size-4 shrink-0', icon)} />
      <div className="min-w-0 flex-1 text-sm">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={cn(title ? 'mt-0.5' : '', 'opacity-90')}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <div role="status" className={cn('flex items-center justify-center gap-2 py-10 text-stone-500', className)}>
      <Loader2 className="size-4 animate-spin" />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center border-y border-stone-200 px-6 py-12 text-center dark:border-stone-800">
      {icon && <div className="mb-3 text-stone-300 dark:text-stone-600 [&_svg]:size-7">{icon}</div>}
      <h3 className="font-semibold">{title}</h3>
      {children && <div className="mt-1 max-w-md text-sm text-stone-500 dark:text-stone-400">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function QueryError({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <Alert
      tone="danger"
      title="Could not load data"
      action={retry && <Button size="sm" variant="outline" onClick={retry}>Retry</Button>}
    >
      {error instanceof Error ? error.message : 'Unknown error'}
    </Alert>
  );
}

export function Stat({ label, value, sub, tone }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: 'danger' | 'warning' | 'success' }) {
  return (
    <div
      className={cn(
        'border-l-2 py-0.5 pl-3',
        tone === 'danger' ? 'border-red-600' : tone === 'warning' ? 'border-amber-500' : tone === 'success' ? 'border-emerald-600' : 'border-stone-300 dark:border-stone-700',
      )}
    >
      <div className="label-caps text-stone-500 dark:text-stone-400">{label}</div>
      <div
        className={cn(
          'mt-1 text-3xl font-semibold tabular-nums tracking-tight',
          tone === 'danger' && 'text-red-700 dark:text-red-400',
          tone === 'warning' && 'text-amber-700 dark:text-amber-400',
          tone === 'success' && 'text-emerald-700 dark:text-emerald-400',
        )}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-stone-500">{sub}</div>}
    </div>
  );
}

export function ProgressBar({ value, className }: { value: number; className?: string }) {
  const pct = Math.round(Math.min(Math.max(value, 0), 1) * 100);
  return (
    <div className={cn('h-1 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-800', className)} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn('h-full rounded-full transition-all', pct === 100 ? 'bg-emerald-600' : 'bg-stone-900 dark:bg-stone-100')} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------------------------

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      className={cn(
        'm-auto w-[calc(100%-1.5rem)] rounded-lg border border-stone-300 bg-white p-0 text-stone-900 shadow-xl backdrop:bg-stone-950/50 dark:border-stone-700 dark:bg-ink-900 dark:text-stone-100',
        size === 'md' && 'max-w-lg',
        size === 'lg' && 'max-w-2xl',
        size === 'xl' && 'max-w-4xl',
      )}
    >
      {open && (
        <div className="flex max-h-[85vh] flex-col">
          <div className="flex items-center justify-between border-b border-stone-200 py-2 pl-4 pr-2 dark:border-stone-800">
            <h2 className="text-base font-semibold">{title}</h2>
            <IconButton label="Close" onClick={onClose}>
              <X className="size-5" />
            </IconButton>
          </div>
          <div className="overflow-y-auto px-4 py-4">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-stone-200 px-4 py-3 dark:border-stone-800">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-x-auto rounded-md border border-stone-200 bg-white dark:border-stone-800 dark:bg-ink-900', className)}>
      <table className="w-full text-left text-sm [&_td]:px-3 [&_td]:py-2.5 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-stone-200 [&_th]:px-3 [&_th]:py-2 [&_th]:font-mono [&_th]:text-[11px] [&_th]:font-medium [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-stone-500 dark:[&_th]:border-stone-800 [&_tbody_tr]:border-t [&_tbody_tr]:border-stone-100 dark:[&_tbody_tr]:border-stone-800 [&_tbody_tr:first-child]:border-t-0">
        {children}
      </table>
    </div>
  );
}
